/**
 * Canvas Placement Roles — single source of truth for R24 / CANVAS_PLACEMENT_LANGUAGE.
 *
 * Algorithms and user mutations declare a `placement_role`; the layout engine
 * owns coordinates. Never write `position.x = …` from discovery/expand/SSE
 * producers — declare a role instead.
 *
 * Six roles:
 *   subject | source | sink | relay | shared_relay | context
 */
export const PLACEMENT_ROLES = [
    'subject',
    'source',
    'sink',
    'relay',
    'shared_relay',
    'context',
] as const;

export type PlacementRole = (typeof PLACEMENT_ROLES)[number];

export const PLACEMENT_ROLE_SET: ReadonlySet<string> = new Set(PLACEMENT_ROLES);

/**
 * Monotonic precedence (CANVAS_PLACEMENT_LANGUAGE §4.3).
 * Higher rank wins; never demote.
 *
 *   context         : 0
 *   source / sink   : 1
 *   relay           : 2
 *   shared_relay    : 3
 *   subject         : 4
 */
export const PLACEMENT_ROLE_RANK: Readonly<Record<PlacementRole, number>> = {
    context: 0,
    source: 1,
    sink: 1,
    relay: 2,
    shared_relay: 3,
    subject: 4,
};

export function isPlacementRole(value: unknown): value is PlacementRole {
    return typeof value === 'string' && PLACEMENT_ROLE_SET.has(value);
}

/** Normalize unknown / invalid roles to `context` (layout default). */
export function normalizePlacementRole(value: unknown): PlacementRole {
    if (isPlacementRole(value)) return value;
    return 'context';
}

/**
 * Pick the higher-precedence role. Missing values fall through.
 * Never demotes an existing role to a lower rank.
 */
export function pickHigherRole(
    existing: string | undefined | null,
    incoming: string | undefined | null,
): string | undefined {
    if (!incoming) return existing ?? undefined;
    if (!existing) return incoming;
    const e = PLACEMENT_ROLE_RANK[normalizePlacementRole(existing)] ?? 0;
    const i = PLACEMENT_ROLE_RANK[normalizePlacementRole(incoming)] ?? 0;
    return i > e ? incoming : existing;
}

/**
 * Infer a frontend-declared role from edge direction relative to a subject
 * (CANVAS_PLACEMENT_LANGUAGE §4.2 — Transfers tab "Add to canvas").
 */
export function inferRoleFromEdgeDirection(
    direction: 'incoming' | 'outgoing' | 'both' | 'unknown' | null | undefined,
): PlacementRole {
    if (direction === 'incoming') return 'source';
    if (direction === 'outgoing') return 'sink';
    if (direction === 'both') return 'relay';
    return 'context';
}

/**
 * Infer role from investigation step (mirrors backend algorithm defaults).
 */
export function inferRoleFromStep(step: string | null | undefined): PlacementRole {
    const s = String(step || '').toLowerCase();
    if (s.includes('backward')) return 'source';
    if (s.includes('forward')) return 'sink';
    if (s.includes('cross_link') || s.includes('holistic')) return 'shared_relay';
    if (s.includes('exchange')) return 'sink';
    if (s.includes('counterparty') || s.includes('exposure')) return 'relay';
    return 'context';
}
